import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X,
  Lock,
  Mail,
  User,
  CheckCircle2,
  AlertCircle,
  Eye,
  EyeOff,
  RefreshCw,
} from 'lucide-react';
import { CameroonFlag } from './CameroonFlag';
import { SocialAuthButtons } from './SocialAuthButtons';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { UserRole } from '../types';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialMode?: 'signin' | 'signup' | 'forgot';
  onLoginSuccess?: (user: { name: string; email: string; role: string }) => void;
  promptMessage?: string;
  actionTitle?: string;
}

export const AuthModal: React.FC<AuthModalProps> = ({
  isOpen,
  onClose,
  initialMode = 'signin',
  onLoginSuccess,
  promptMessage,
  actionTitle,
}) => {
  const navigate = useNavigate();
  const { signIn, signUp, sendPasswordReset } = useAuth();
  const { language } = useLanguage();
  const isFr = language === 'fr';

  const [mode, setMode] = useState<'signin' | 'signup' | 'forgot'>(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('690 12 34 56');
  const [accountType, setAccountType] = useState<UserRole>('Buyer');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setMode(initialMode);
      setErrorMessage(null);
      setSuccessMessage(null);
    }
  }, [isOpen, initialMode]);

  const rolesList: { id: UserRole; label: string }[] = isFr
    ? [
        { id: 'Buyer', label: 'Acheteur / Investisseur' },
        { id: 'Tenant', label: 'Locataire' },
        { id: 'Seller', label: 'Propriétaire / Vendeur' },
        { id: 'Agent', label: 'Agent Immobilier' },
        { id: 'Professional', label: 'Notaire / Géomètre' },
      ]
    : [
        { id: 'Buyer', label: 'Buyer / Investor' },
        { id: 'Tenant', label: 'Tenant' },
        { id: 'Seller', label: 'Landowner / Seller' },
        { id: 'Agent', label: 'Real Estate Agent' },
        { id: 'Professional', label: 'Notary / Surveyor' },
      ];

  if (!isOpen) return null;

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    setIsLoading(true);

    const cleanEmail = email.trim().toLowerCase();
    const res = await signIn(cleanEmail, password);
    setIsLoading(false);

    if (res.success) {
      setSuccessMessage(isFr ? 'Connexion réussie !' : 'Signed in successfully!');
      onLoginSuccess?.({
        name: cleanEmail.includes('@') ? cleanEmail.split('@')[0] : (cleanEmail || 'User'),
        email: cleanEmail,
        role: 'Buyer',
      });
      setTimeout(() => {
        onClose();
      }, 1000);
    } else {
      setErrorMessage(
        res.error || (isFr ? 'Échec de la connexion.' : 'Failed to sign in.')
      );
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    if (password.length < 8) {
      setErrorMessage(
        isFr
          ? 'Le mot de passe doit contenir au moins 8 caractères.'
          : 'Password must be at least 8 characters.'
      );
      return;
    }

    if (!/[A-Za-z]/.test(password) || !/[0-9]/.test(password)) {
      setErrorMessage(
        isFr
          ? 'Le mot de passe doit contenir au moins une lettre et un chiffre.'
          : 'Password must contain at least one letter and one number.'
      );
      return;
    }

    setIsLoading(true);

    const cleanEmail = email.trim().toLowerCase();
    const res = await signUp(cleanEmail, password, {
      fullName: fullName.trim() || (cleanEmail.includes('@') ? cleanEmail.split('@')[0] : 'User'),
      role: accountType,
      phone: phone.trim() || '',
    });
    setIsLoading(false);

    if (res.success) {
      setSuccessMessage(
        isFr
          ? 'Compte créé ! Un email de vérification a été envoyé. Veuillez cliquer sur le lien pour activer votre compte avant de vous connecter.'
          : 'Account created! A verification email has been sent. Please click the link to activate your account before logging in.'
      );
      setMode('signin');
    } else {
      setErrorMessage(
        res.error || (isFr ? 'Échec de la création du compte.' : 'Failed to create account.')
      );
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail) {
      setErrorMessage(
        isFr
          ? 'Veuillez saisir votre adresse email.'
          : 'Please enter your registered email address.'
      );
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);
    const res = await sendPasswordReset(cleanEmail);
    setIsLoading(false);

    if (res.success) {
      setSuccessMessage(
        res.message ||
          (isFr
            ? 'Lien de réinitialisation envoyé par email.'
            : 'Password reset link sent to your email.')
      );
    } else {
      setErrorMessage(
        res.error ||
          (isFr
            ? 'Impossible d’envoyer le lien de réinitialisation.'
            : 'Unable to send password reset link.')
      );
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-md w-full border border-slate-200 shadow-2xl animate-in zoom-in-95 duration-150">
        {/* Header */}
        <div className="p-5 bg-gradient-to-r from-[#072f1c] to-[#094426] text-white flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-2.5">
            <CameroonFlag className="w-5 h-3.5" />
            <div>
              <h3 className="text-base font-bold">
                {mode === 'signin'
                  ? isFr
                    ? 'Connexion à Cameroon Land Market'
                    : 'Sign In to Cameroon Land Market'
                  : mode === 'signup'
                  ? isFr
                    ? 'Créer un Compte'
                    : 'Create an Account'
                  : isFr
                  ? 'Réinitialiser le Mot de Passe'
                  : 'Reset Password'}
              </h3>
              <p className="text-xs text-emerald-200/80">
                {mode === 'signin'
                  ? isFr
                    ? 'Accédez à vos biens sauvegardés et demandes'
                    : 'Access your saved properties and inquiries'
                  : mode === 'signup'
                  ? isFr
                    ? 'Acheter, louer, vendre ou vérifier un Titre Foncier'
                    : 'Buy, rent, sell, or verify cadastral deeds'
                  : isFr
                  ? 'Récupérez l’accès à votre compte en toute sécurité'
                  : 'Recover your account access securely'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors cursor-pointer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Tab Switcher */}
        <div className="flex border-b border-slate-200">
          <button
            type="button"
            onClick={() => {
              setMode('signin');
              setErrorMessage(null);
              setSuccessMessage(null);
            }}
            className={`flex-1 py-3 text-xs font-bold text-center transition-colors cursor-pointer ${
              mode === 'signin'
                ? 'border-b-2 border-[#094426] text-[#094426] bg-emerald-50/40'
                : 'text-slate-500 hover:text-slate-900'
            }`}
          >
            {isFr ? 'Connexion' : 'Sign In'}
          </button>
          <button
            type="button"
            onClick={() => {
              setMode('signup');
              setErrorMessage(null);
              setSuccessMessage(null);
            }}
            className={`flex-1 py-3 text-xs font-bold text-center transition-colors cursor-pointer ${
              mode === 'signup'
                ? 'border-b-2 border-[#094426] text-[#094426] bg-emerald-50/40'
                : 'text-slate-500 hover:text-slate-900'
            }`}
          >
            {isFr ? 'Créer un Compte' : 'Create Account'}
          </button>
        </div>

        {/* Form Body */}
        <div className="p-6">
          {promptMessage && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 text-amber-900 rounded-xl text-xs flex items-center gap-2.5 font-medium shadow-2xs">
              <Lock className="w-4 h-4 text-amber-700 shrink-0" />
              <span>{promptMessage}</span>
            </div>
          )}

          {errorMessage && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-800 rounded-xl text-xs flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
              <span>{errorMessage}</span>
            </div>
          )}

          {successMessage && (
            <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl text-xs flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
              <span>{successMessage}</span>
            </div>
          )}

          {/* SIGN IN FORM */}
          {mode === 'signin' && (
            <form onSubmit={handleSignIn} className="space-y-3.5">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  {isFr ? 'Adresse Email' : 'Email Address'}
                </label>
                <div className="relative">
                  <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="user@example.cm"
                    className="w-full text-xs py-2.5 pl-9 pr-3 bg-slate-50 border border-slate-300 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#094426]"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-semibold text-slate-700">
                    {isFr ? 'Mot de Passe' : 'Password'}
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      setMode('forgot');
                      setErrorMessage(null);
                    }}
                    className="text-xs text-[#094426] font-semibold hover:underline cursor-pointer"
                  >
                    {isFr ? 'Mot de passe oublié ?' : 'Forgot password?'}
                  </button>
                </div>
                <div className="relative">
                  <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full text-xs py-2.5 pl-9 pr-10 bg-slate-50 border border-slate-300 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#094426]"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-3 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-[#094426] hover:bg-[#07361e] text-white font-bold py-2.5 rounded-xl text-xs transition-colors shadow-xs mt-2 cursor-pointer flex items-center justify-center gap-2 disabled:opacity-70"
              >
                {isLoading ? (
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <span>{isFr ? 'Se Connecter' : 'Sign In'}</span>
                )}
              </button>

              {/* Or continue with Social */}
              <div className="my-3 flex items-center gap-2">
                <div className="flex-1 h-px bg-slate-200" />
                <span className="text-[10px] text-slate-400 font-light lowercase">
                  {isFr ? 'ou continuer avec' : 'or continue with'}
                </span>
                <div className="flex-1 h-px bg-slate-200" />
              </div>

              <SocialAuthButtons
                mode="signin"
                onSuccess={(u) => {
                  setSuccessMessage(isFr ? 'Connexion réussie !' : 'Signed in successfully!');
                  onLoginSuccess?.(u);
                  setTimeout(() => onClose(), 800);
                }}
                onError={(err) => setErrorMessage(err)}
              />
            </form>
          )}

          {/* SIGN UP FORM */}
          {mode === 'signup' && (
            <form onSubmit={handleSignUp} className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  {isFr ? 'Rôle du Compte :' : 'Select Account Role:'}
                </label>
                <div className="grid grid-cols-3 gap-1.5 text-xs">
                  {rolesList.map((role) => (
                    <button
                      key={role.id}
                      type="button"
                      onClick={() => setAccountType(role.id)}
                      className={`p-1.5 rounded-lg border text-center transition-all cursor-pointer ${
                        accountType === role.id
                          ? 'border-[#094426] bg-emerald-50 text-[#094426] font-bold ring-1 ring-emerald-300'
                          : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      <span className="text-[10px] leading-tight block">{role.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  {isFr ? 'Nom Complet' : 'Full Name'}
                </label>
                <div className="relative">
                  <User className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
                  <input
                    type="text"
                    required
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder={isFr ? 'ex: Marc Kamga' : 'e.g. Marc Kamga'}
                    className="w-full text-xs py-2 pl-9 pr-3 bg-slate-50 border border-slate-300 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#094426]"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  {isFr ? 'Adresse Email' : 'Email'}
                </label>
                <div className="relative">
                  <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="user@example.cm"
                    className="w-full text-xs py-2 pl-9 pr-3 bg-slate-50 border border-slate-300 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#094426]"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  {isFr ? 'Mot de passe (min. 8 car., lettre & chiffre)' : 'Password (min 8, letter & number)'}
                </label>
                <div className="relative">
                  <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full text-xs py-2 pl-9 pr-10 bg-slate-50 border border-slate-300 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#094426]"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-[#094426] hover:bg-[#07361e] text-white font-bold py-2.5 rounded-xl text-xs transition-colors shadow-xs mt-2 cursor-pointer flex items-center justify-center gap-2 disabled:opacity-70"
              >
                {isLoading ? (
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <span>
                    {isFr
                      ? `S'inscrire comme ${rolesList.find((r) => r.id === accountType)?.label || accountType}`
                      : `Register as ${accountType}`}
                  </span>
                )}
              </button>

              {/* Or register with Social */}
              <div className="my-3 flex items-center gap-2">
                <div className="flex-1 h-px bg-slate-200" />
                <span className="text-[10px] text-slate-400 font-light lowercase">
                  {isFr ? 'ou s’inscrire avec' : 'or register with'}
                </span>
                <div className="flex-1 h-px bg-slate-200" />
              </div>

              <SocialAuthButtons
                mode="signup"
                selectedRole={accountType}
                onSuccess={(u) => {
                  setSuccessMessage(isFr ? 'Compte créé avec succès !' : 'Account created successfully!');
                  onLoginSuccess?.(u);
                  setTimeout(() => onClose(), 800);
                }}
                onError={(err) => setErrorMessage(err)}
              />
            </form>
          )}

          {/* FORGOT PASSWORD FORM */}
          {mode === 'forgot' && (
            <div>
              {successMessage ? (
                <div className="space-y-4 text-center py-2">
                  <div className="w-12 h-12 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center mx-auto">
                    <CheckCircle2 className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-slate-900">
                      {isFr ? 'Lien de Réinitialisation Envoyé !' : 'Reset Link Sent!'}
                    </h4>
                    <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                      {successMessage}
                    </p>
                    <p className="text-[11px] text-slate-500 mt-2 bg-slate-50 p-2.5 rounded-xl border border-slate-200">
                      {isFr
                        ? '👉 Ouvrez votre e-mail et cliquez sur le lien sécurisé pour définir votre nouveau mot de passe.'
                        : '👉 Open your email and click the secure link to set your new password.'}
                    </p>
                  </div>
                  <div className="pt-2 space-y-2">
                    <button
                      type="button"
                      onClick={() => {
                        setMode('signin');
                        setSuccessMessage(null);
                      }}
                      className="w-full bg-[#094426] hover:bg-[#07361e] text-white font-bold py-2.5 rounded-xl text-xs transition-colors cursor-pointer"
                    >
                      {isFr ? 'Retour à la Connexion' : 'Back to Sign In'}
                    </button>
                    <button
                      type="button"
                      onClick={handleForgotPassword}
                      disabled={isLoading}
                      className="w-full text-slate-600 hover:text-slate-900 text-xs font-semibold py-1.5 cursor-pointer"
                    >
                      {isLoading
                        ? (isFr ? 'Renvoi...' : 'Resending...')
                        : (isFr ? 'Renvoyer l’e-mail' : 'Resend Email')}
                    </button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <p className="text-xs text-slate-600">
                    {isFr
                      ? 'Entrez votre adresse email enregistrée et nous vous enverrons un lien de réinitialisation sécurisé.'
                      : 'Enter your registered email address and we will dispatch a secure password reset link.'}
                  </p>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">
                      {isFr ? 'Email Enregistré' : 'Registered Email'}
                    </label>
                    <div className="relative">
                      <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
                      <input
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="user@example.cm"
                        className="w-full text-xs py-2.5 pl-9 pr-3 bg-slate-50 border border-slate-300 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#094426]"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-[#094426] hover:bg-[#07361e] text-white font-bold py-2.5 rounded-xl text-xs transition-colors shadow-xs cursor-pointer flex items-center justify-center gap-2 disabled:opacity-70"
                  >
                    {isLoading ? (
                      <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <span>{isFr ? 'Envoyer le Lien de Réinitialisation' : 'Send Password Reset Link'}</span>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={() => setMode('signin')}
                    className="w-full text-center text-xs text-slate-500 hover:text-slate-900 font-semibold cursor-pointer"
                  >
                    {isFr ? 'Retour à la Connexion' : 'Back to Sign In'}
                  </button>
                </form>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
